import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";

type Schemas = components["schemas"];
export type StatusReason = Schemas["StatusReason"];
export type ReasonScope = StatusReason["applies_to"];

/** The statuses a company keeps reasons for (P15-14), as staff read them. */
export const REASON_SCOPE: Record<ReasonScope, { label: string; help: string }> = {
  order_cancelled: { label: "Cancelling an order", help: "Asked for when staff cancel an order." },
  delivery_cancelled: {
    label: "Cancelling a delivery",
    help: "Asked for when a suspended delivery is closed as cancelled.",
  },
  delivery_suspended: { label: "Suspending a delivery", help: "Asked for when a delivery is put on hold." },
  delivery_rerouted: { label: "Rerouting a delivery", help: "Asked for when a delivery is sent elsewhere." },
};
export const REASON_SCOPES = Object.keys(REASON_SCOPE) as ReasonScope[];

/** Active reasons for one status, for the pickers; every reason, retired too, for the settings page. */
export const reasonsQuery = (scope?: ReasonScope, includeInactive = false) =>
  queryOptions({
    queryKey: ["status-reasons", scope ?? "all", includeInactive],
    staleTime: 5 * 60_000,
    queryFn: async () =>
      (
        await ok(
          api.GET("/v1/admin/orders/reasons", {
            params: { query: { applies_to: scope, include_inactive: includeInactive ? "true" : undefined } },
          }),
        )
      ).data,
  });

export const reasonActions = {
  create: (body: Schemas["StatusReasonBody"]) => ok(api.POST("/v1/admin/orders/reasons", { body })),
  update: (id: string, body: Schemas["StatusReasonUpdateBody"]) =>
    ok(api.PATCH("/v1/admin/orders/reasons/{id}", { params: { path: { id } }, body })),
};

/** A reason's code from its name: "Gate locked" → gate_locked (lowercase letters, digits and _). */
export function codeFor(name: string): string {
  const code = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  return /^[a-z]/.test(code) ? code : `r_${code}`.slice(0, 40);
}

/**
 * What a reason picker sends, and what's missing. With the company's reasons for the status, one
 * must be picked, and "Other" needs a note; the free text is the note, else the reason's name.
 * With none set up, the note alone is the reason, as before.
 */
export function reasonChoice(reasons: StatusReason[], reasonId: string, note: string) {
  const picked = reasons.find((r) => r.id === reasonId);
  const text = note.trim() || picked?.name || "";
  let missing: string | undefined;
  if (reasons.length > 0 && !picked) missing = "Pick a reason.";
  else if (picked?.code === "other" && !note.trim()) missing = "Say what happened.";
  else if (!text) missing = "Say why: it goes on the timeline.";
  return { text, reasonId: picked?.id, missing };
}
